import { ContainerLevelHistoryEntity, inchesToFeet, LevelEntity, LevelType } from "@ots-frontend/ots-data";

/**
 * Return the displayble value of the container level.
 *
 * @param containerLevel The container level.
 */
export const getDisplayableLevelValue = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity | undefined,
  levelType: LevelType
): string | undefined => {
  let displayValue: string | undefined = undefined;

  if (isLevelDisplayable(containerLevel, levelType) && containerLevel) {
    if (levelType === "height") {
      displayValue = getDisplayableHeightLevelValue(containerLevel);
    } else if (levelType === "volume") {
      displayValue = getDisplayableVolumeLevelValue(containerLevel);
    } else {
      displayValue = "-";
    }
  } else if (containerLevel) {
    displayValue = "-";
  }

  return displayValue;
};

/**
 * Return the displayble value of the container level.
 *
 * @param containerLevel The container level.
 */
const getDisplayableHeightLevelValue = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity
): string | undefined => {
  return getDisplayableHeightValue(containerLevel?.height)
};

export const getDisplayableHeightValue = (height : number | undefined): string | undefined => {
  // TODO: more conversions
  if (height) {
    const feetInches = inchesToFeet(height);
    return `${feetInches.feet.toFixed(0)}' ${feetInches.inches.toFixed(
      0
    )}" ${feetInches.quarters.toFixed(0)}/4`;
  }

  return undefined;
};

/**
 * Return the displayble value of the container level.
 *
 * @param containerLevel The container level.
 */
const getDisplayableVolumeLevelValue = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity
): string | undefined => {
  return containerLevel.volume?.toFixed(2);
};

/**
 * Returns true if the container level can be displayed.
 *
 * @param containerLevel The container level.
 */
export const isLevelDisplayable = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity | undefined,
  levelType: LevelType
): boolean => {
  if (levelType === "height") {
    return isHeightLevelDisplayable(containerLevel);
  } else if (levelType === "volume") {
    return isVolumeLevelDisplayable(containerLevel);
  }

  return false;
};

/**
 * Returns true if the container level can be displayed.
 *
 * @param containerLevel The container level.
 */
export const isHeightLevelDisplayable = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity | undefined
): boolean => {
  return (
    containerLevel !== undefined &&
    containerLevel.height !== undefined &&
    containerLevel.heightUom !== undefined &&
    containerLevel.heightUom === "inches" &&
    (isLevelEntity(containerLevel)
      ? containerLevel.dttm !== undefined
      : containerLevel.createDttm !== undefined)
  );
};

/**
 * Returns true if the container level can be displayed.
 *
 * @param containerLevel The container level.
 */
export const isVolumeLevelDisplayable = (
  containerLevel: LevelEntity | ContainerLevelHistoryEntity | undefined
): boolean => {
  return (
    containerLevel !== undefined &&
    containerLevel.volume !== undefined &&
    containerLevel.volumeUom !== undefined &&
    containerLevel.volumeUom === "barrels" &&
    (isLevelEntity(containerLevel)
      ? containerLevel.dttm !== undefined
      : containerLevel.createDttm !== undefined)
  );
};

/**
 * Returns true if the value is a container level.
 *
 * @param value The container level or container level history.
 */
const isLevelEntity = (
  value: LevelEntity | ContainerLevelHistoryEntity
): value is LevelEntity => {
  if ("dttm" in value) {
    return true;
  }

  return false;
};
